// src/components/EnrolledCompetitionCard.jsx
// Competition card for enrolled/registered competitions (MyCompetitions page).
// Shares banner/header with CompetitionCard but displays team selection, match day, and status info.

import { Link } from 'react-router-dom';
import CompetitionCardHeader from './CompetitionCardHeader';
import { Card as FcCard, LinkButton as FcLinkButton } from '../ui';

function ChevronRightIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <polyline points="9 18 15 12 9 6"/>
    </svg>
  );
}
function LockIcon({ size = 12 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
      <rect x="3" y="11" width="18" height="10" rx="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );
}

/**
 * Competition card for enrolled competitions.
 * @param {object}   sel                          – user selection record
 * @param {object}   sel.competitions             – joined competition details
 * @param {number}   sel.competition_id           – competition ID
 * @param {string}   sel.league                   – league name
 * @param {string}   sel.season                   – season
 * @param {string}   sel.status                   – user status in competition
 * @param {string}   selectedTeam                 – selected team name for this matchday (optional)
 * @param {boolean}  isSelectionLocked           – whether team selection is locked
 * @param {boolean}  isEliminated                – true if knockout + eliminated
 * @param {function} getRoute                    – (id, type) → route string
 */
export default function EnrolledCompetitionCard({
  sel,
  selectedTeam,
  isSelectionLocked,
  isEliminated,
  getRoute,
}) {
  const comp = sel.competitions || {};
  const compWithLeague = { ...comp, league: sel.league };
  const route = getRoute(sel.competition_id, comp.type);
  const isWorldStage = comp.type === 'world_stage';
  const isPrivate = comp.visibility === 'private';

  return (
    <FcCard as="div" padding="none" className="bg-surface-card rounded-xl shadow-sm border border-ui-border overflow-hidden flex flex-col group hover:shadow-md transition-shadow duration-200">

      {/* ── Banner (shared header) ── */}
      <CompetitionCardHeader comp={compWithLeague} leagueBranding />

      {/* ── Content ── */}
      <div className="flex-1 flex flex-col px-4 pt-3 pb-4 gap-0">

        {/* Name + status badges */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-bold text-sm leading-snug text-content-primary">{comp.name || 'Unnamed'}</h3>
          {(comp.is_18_plus || isPrivate) && (
            <div className="flex items-center gap-1.5 shrink-0 mt-0.5 text-xs">
              {comp.is_18_plus && (
                <span className="rounded bg-red-600 text-white font-bold tracking-wide px-1.5 py-0.5">
                  18+
                </span>
              )}
              {isPrivate && (
                <span className="p-1 rounded-full bg-gray-100 text-content-secondary border border-ui-border">
                  <LockIcon size={12} />
                </span>
              )}
            </div>
          )}
        </div>

        {/* Season & Match Day/Stage row */}
        <div className="flex items-center justify-between mb-3 text-sm">
          <div>
            <span className="text-content-secondary font-medium">Season: </span>
            <span className="text-content-primary font-semibold">{sel.season}</span>
          </div>
          <div>
            <span className="text-content-secondary font-medium">{isWorldStage ? 'Stage' : 'MD'}: </span>
            <span className="text-content-primary font-semibold">
              {isWorldStage
                ? (comp.current_stage || 'TBA')
                : (comp.current_match_day || sel.match_day || '-')}
            </span>
          </div>
        </div>

        {/* Selected team row – not shown for world_stage */}
        {!isWorldStage && (
          <div className="mb-3 p-3 rounded-lg bg-gray-50 border border-ui-border">
            {selectedTeam ? (
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-xs text-content-secondary block mb-1">Selected Team</span>
                  <span className="text-sm font-bold text-content-primary">{selectedTeam}</span>
                </div>
              </div>
            ) : (
              <span className="text-xs text-content-secondary">No team selected</span>
            )}
          </div>
        )}

        {/* Status badge – shown if eliminated or world_stage */}
        {isEliminated && (
          <div className="mb-3 p-2 rounded-lg bg-red-50 border border-red-200">
            <span className="text-xs font-bold text-ui-error">Status: Eliminated</span>
          </div>
        )}

        {/* CTA buttons */}
        <div className="fc-card-actions">
          {isWorldStage ? (
            <FcLinkButton variant="primary" size="compact"
              to={route}
            >
              Open World Stage <ChevronRightIcon size={14} />
            </FcLinkButton>
          ) : (
            <>
              {!isEliminated && !isSelectionLocked && (
                <FcLinkButton variant="primary" size="compact"
                  to={`/select-team/${sel.competition_id}`}
                >
                  {selectedTeam ? 'Edit' : 'Select'} Team <ChevronRightIcon size={14} />
                </FcLinkButton>
              )}
              <FcLinkButton variant="secondary" size="compact"
                to={route}
              >
                View Competition <ChevronRightIcon size={14} />
              </FcLinkButton>
            </>
          )}
        </div>
      </div>
    </FcCard>
  );
}
